import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Upload, FileText, Loader2, Sparkles, RefreshCw, Search, CheckCircle2 } from 'lucide-react';
import DatePickerField from './DatePickerField';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const inputCls = 'w-full bg-white border border-slate-300 rounded-lg px-3 py-2 text-sm text-slate-900 placeholder:text-slate-500 focus:outline-none focus:border-blue-500';
const STATUS = {
  expired: ['Expired', 'bg-red-50 text-red-700 border-red-200'],
  expiring_30: ['Expiring', 'bg-amber-50 text-amber-700 border-amber-200'],
  expiring_60: ['Expiring', 'bg-amber-50 text-amber-700 border-amber-200'],
  expiring_90: ['Expiring', 'bg-amber-50 text-amber-700 border-amber-200'],
  active: ['Valid', 'bg-emerald-50 text-emerald-700 border-emerald-200'],
  no_expiry: ['No expiry', 'bg-slate-200 text-slate-700 border-slate-500/40'],
  superseded: ['Replaced', 'bg-slate-200 text-slate-500 border-slate-300'],
};

const EmployeePicker = ({ value, onChange }) => {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  useEffect(() => {
    axios.get(`${API}/users`, auth()).then((r) => setUsers((r.data.users || []).filter((u) => u.is_active !== false && u.role !== 'customer' && u.full_name)
      .sort((a, b) => a.full_name.localeCompare(b.full_name)))).catch(() => {});
  }, []);
  const sel = users.find((u) => u.id === value);
  const list = users.filter((u) => !q || u.full_name.toLowerCase().includes(q.toLowerCase())).slice(0, 12);
  return (
    <div className="relative">
      <div className="relative">
        <Search className="w-4 h-4 text-slate-500 absolute left-3 top-2.5" />
        <input value={open ? q : (sel?.full_name || '')} onChange={(e) => { setQ(e.target.value); setOpen(true); }} onFocus={() => { setQ(''); setOpen(true); }}
          onBlur={() => setTimeout(() => setOpen(false), 150)} placeholder="Type a name…" className={`${inputCls} pl-9`} data-testid="tu-employee-input" />
      </div>
      {open && list.length > 0 && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-slate-300 rounded-lg shadow-2xl max-h-56 overflow-y-auto" data-testid="tu-employee-list">
          {list.map((u) => (
            <button key={u.id} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => { onChange(u.id); setOpen(false); }}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 ${u.id === value ? 'text-blue-700' : 'text-slate-800'}`} data-testid={`tu-employee-opt-${u.id}`}>
              {u.full_name} <span className="text-xs text-slate-500">{(u.role || '').replace(/_/g, ' ')}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Ticket name with autocomplete: the person's existing tickets (pick one = it's a renewal) + standard course names; free text allowed.
const TicketNameInput = ({ value, onChange, suggestions, renewsId, onRenew }) => {
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(-1);
  const q = (value || '').toLowerCase().trim();
  const existing = (suggestions?.existing || []).filter((t) => t.status !== 'superseded' && (!q || t.name.toLowerCase().includes(q) || (t.topic_labels || []).some((l) => l.toLowerCase().includes(q))));
  const heldLabels = new Set((suggestions?.existing || []).flatMap((t) => t.topic_labels || []));
  const standard = (suggestions?.standard || []).filter((s) => !heldLabels.has(s) && (!q || s.toLowerCase().includes(q))).slice(0, 8);
  const items = [...existing.map((t) => ({ kind: 'existing', t })), ...standard.map((s) => ({ kind: 'standard', s }))];
  const pick = (it) => {
    if (it.kind === 'existing') { onChange(it.t.topic_labels?.[0] || it.t.name); onRenew(it.t.id); }
    else { onChange(it.s); }
    setOpen(false);
  };
  const onKey = (e) => {
    if (!open || !items.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setHi((h) => Math.min(h + 1, items.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter' && hi >= 0) { e.preventDefault(); pick(items[hi]); }
    else if (e.key === 'Escape') setOpen(false);
  };
  return (
    <div className="relative">
      <input value={value} onChange={(e) => { onChange(e.target.value); setOpen(true); setHi(-1); }} onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)} onKeyDown={onKey} placeholder="e.g. First Aid / CPR" className={inputCls}
        autoComplete="off" data-testid="tu-ticket-name" />
      {open && items.length > 0 && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-slate-300 rounded-lg shadow-2xl max-h-64 overflow-y-auto" data-testid="tu-suggestions">
          {existing.length > 0 && <p className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider text-purple-700 font-bold">Renew one of their tickets</p>}
          {items.map((it, i) => it.kind === 'existing' ? (
            <button key={`e-${it.t.id}`} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => pick(it)}
              className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between gap-2 ${hi === i ? 'bg-white' : 'hover:bg-slate-50'} ${it.t.id === renewsId ? 'text-purple-700' : 'text-slate-800'}`} data-testid={`tu-suggest-existing-${it.t.id}`}>
              <span className="min-w-0">
                <span className="block truncate">{it.t.topic_labels?.[0] || it.t.name}</span>
                <span className="block text-[11px] text-slate-500 truncate">📁 {it.t.filename || it.t.name}</span>
              </span>
              <span className="flex items-center gap-1 shrink-0">
                {it.t.expiry && <span className="text-[11px] text-slate-500">{it.t.expiry}</span>}
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${(STATUS[it.t.status] || STATUS.active)[1]}`}>{(STATUS[it.t.status] || STATUS.active)[0]}</span>
              </span>
            </button>
          ) : (
            <React.Fragment key={`s-${it.s}`}>
              {i === existing.length && <p className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider text-slate-500 font-bold">{existing.length ? 'Or a new ticket' : 'Standard names'}</p>}
              <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => pick(it)}
                className={`w-full text-left px-3 py-1.5 text-sm text-slate-800 ${hi === i ? 'bg-white' : 'hover:bg-slate-50'}`} data-testid={`tu-suggest-standard-${it.s}`}>{it.s}</button>
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
};

// Upload ONE card/certificate to an employee's Drive "Tickets & Certificates" folder and record it right away.
export default function TicketUploadModal({ employeeId: fixedEmployeeId, employeeName, onClose, onDone }) {
  const [employeeId, setEmployeeId] = useState(fixedEmployeeId || '');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [reading, setReading] = useState(false);
  const [read, setRead] = useState(null);
  const [name, setName] = useState('');
  const [expiry, setExpiry] = useState('');
  const [issued, setIssued] = useState('');
  const [note, setNote] = useState('');
  const [renewsId, setRenewsId] = useState('');
  const [sugg, setSugg] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!employeeId) { setSugg(null); return; }
    axios.get(`${API}/ticket-upload/suggestions`, { params: { employee_id: employeeId }, ...auth() }).then((r) => setSugg(r.data)).catch(() => setSugg(null));
  }, [employeeId]);

  // Card read says "First Aid / CPR" and they already hold one → default to renewing the newest of those
  useEffect(() => {
    if (!sugg || !read?.suggested_label || renewsId) return;
    const match = sugg.existing.find((t) => t.status !== 'superseded' && (t.topic_labels || []).includes(read.suggested_label));
    if (match) setRenewsId(match.id);
  }, [sugg, read]); // eslint-disable-line

  const renewing = useMemo(() => (sugg?.existing || []).find((t) => t.id === renewsId), [sugg, renewsId]);

  const onFile = async (f) => {
    if (!f) return;
    setFile(f);
    setRead(null);
    setPreview(f.type.startsWith('image/') ? URL.createObjectURL(f) : null);
    setReading(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const r = await axios.post(`${API}/ticket-upload/inspect`, fd, auth());
      const d = r.data;
      if (d.ok && d.found) {
        setRead(d);
        if (!name) setName(d.suggested_label || d.ticket_name || '');
        if (!expiry && d.expiry_date) setExpiry(d.expiry_date);
        if (!fixedEmployeeId && !employeeId && d.matched_employee_id) setEmployeeId(d.matched_employee_id);
      } else if (d.ok === false) toast.message(d.detail);
    } catch { /* manual entry still works */ }
    setReading(false);
  };

  const submit = async () => {
    if (!employeeId) { toast.error('Pick the employee'); return; }
    if (!file) { toast.error('Add the card photo or PDF'); return; }
    if (!name.trim()) { toast.error('Give the ticket a name'); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('employee_id', employeeId);
      fd.append('ticket_name', name.trim());
      if (expiry) fd.append('expiry_date', expiry);
      if (issued) fd.append('issue_date', issued);
      if (note.trim()) fd.append('note', note.trim());
      if (renewsId) fd.append('renews_ticket_id', renewsId);
      const r = await axios.post(`${API}/ticket-upload`, fd, auth());
      const who = sugg?.employee?.name || employeeName || 'the employee';
      let msg = `Filed “${name.trim()}” for ${who} → Drive ✓`;
      if (r.data.old_archived) msg += ` · old card moved to Expired Tickets`;
      else if (r.data.old_name && r.data.old_error) msg += ` · couldn't move the old card (${r.data.old_error})`;
      if (r.data.folder_created) msg += ` · created their Drive folder`;
      toast.success(msg, { duration: 8000 });
      onDone?.(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    setSaving(false);
  };

  const mismatch = read?.employee_name && sugg?.employee?.name && read.matched_employee_id && read.matched_employee_id !== employeeId;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[70] bg-black/70 flex items-center justify-center p-4" data-testid="ticket-upload-modal">
      <div className="bg-white border border-slate-200 rounded-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto shadow-2xl text-slate-800">
        <div className="flex items-start justify-between gap-3 p-5 border-b border-slate-200">
          <div>
            <h3 className="text-slate-900 font-bold text-base md:text-lg flex items-center gap-2"><Upload className="w-5 h-5 text-blue-700" /> Upload a ticket{employeeName ? ` — ${employeeName}` : ''}</h3>
            <p className="text-xs text-slate-500 mt-1">Goes straight into their Drive “Tickets &amp; Certificates” folder and onto their record — no rescan needed.</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-slate-500 hover:text-slate-900 hover:bg-slate-50" data-testid="tu-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          {!fixedEmployeeId && (
            <label className="block">
              <span className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Employee</span>
              <div className="mt-1"><EmployeePicker value={employeeId} onChange={(id) => { setEmployeeId(id); setRenewsId(''); }} /></div>
            </label>
          )}
          <div>
            <span className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Card photo or PDF</span>
            <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} data-testid="tu-file-input" />
            <div onClick={() => fileRef.current?.click()} onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); onFile(e.dataTransfer.files?.[0]); }}
              className={`mt-1 rounded-xl border-2 border-dashed p-4 cursor-pointer flex items-center gap-4 ${file ? 'border-blue-300 bg-blue-50' : 'border-slate-300 hover:border-slate-400 bg-white'}`} data-testid="tu-dropzone">
              {preview ? <img src={preview} alt="card" className="w-28 h-20 object-contain bg-white rounded-lg shrink-0" data-testid="tu-preview" />
                : <div className="w-28 h-20 rounded-lg bg-white flex items-center justify-center shrink-0">{file ? <FileText className="w-8 h-8 text-red-700" /> : <Upload className="w-8 h-8 text-slate-500" />}</div>}
              <div className="min-w-0 text-sm">
                {file ? <p className="text-slate-900 font-medium truncate" data-testid="tu-file-name">{file.name}</p> : <p className="text-slate-800 font-medium">Drop the card here, or tap to choose / take a photo</p>}
                {reading ? <p className="text-xs text-blue-700 flex items-center gap-1 mt-1" data-testid="tu-reading"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Reading the card…</p>
                  : read?.found ? <p className="text-xs text-emerald-700 flex items-center gap-1 mt-1" data-testid="tu-read-ok"><Sparkles className="w-3.5 h-3.5" /> Read: {read.ticket_name || '?'}{read.expiry_date ? ` · exp ${read.expiry_date}` : ''}{read.employee_name ? ` · ${read.employee_name}` : ''} — check and fix below</p>
                  : read && !read.found ? <p className="text-xs text-slate-500 mt-1">Couldn't make out a card — fill it in below</p>
                  : <p className="text-xs text-slate-500 mt-1">JPG, PNG, HEIC or PDF · the card is read automatically to prefill the name and expiry</p>}
                {mismatch && <p className="text-xs text-amber-700 mt-1" data-testid="tu-mismatch">⚠️ The card reads “{read.employee_name}” — you're filing it under {sugg.employee.name}</p>}
              </div>
            </div>
          </div>
          <div>
            <span className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Ticket name</span>
            <div className="mt-1"><TicketNameInput value={name} onChange={(v) => { setName(v); }} suggestions={sugg} renewsId={renewsId} onRenew={setRenewsId} /></div>
            {renewing ? (
              <p className="mt-1.5 text-xs text-purple-800 bg-purple-50 border border-purple-200 rounded-lg px-3 py-1.5 flex items-center justify-between gap-2" data-testid="tu-renewing">
                <span className="flex items-center gap-1 min-w-0"><RefreshCw className="w-3.5 h-3.5 shrink-0" /> <span className="truncate">Renews “{renewing.name}”{renewing.expiry ? ` (${renewing.expiry})` : ''} — the old card moves to Expired Tickets</span></span>
                <button onClick={() => setRenewsId('')} className="text-purple-700 hover:text-slate-900 shrink-0" title="This is a new ticket, not a renewal" data-testid="tu-renewing-clear"><X className="w-3.5 h-3.5" /></button>
              </p>
            ) : name.trim() && sugg ? <p className="mt-1.5 text-xs text-slate-500" data-testid="tu-new-ticket">New ticket for {sugg.employee.name} — pick one from the list if it replaces an existing card.</p> : null}
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Expiry date</span>
              <DatePickerField value={expiry} onChange={setExpiry} placeholder="No expiry / unknown" className="mt-1 w-full bg-white rounded-lg px-3 py-2 text-sm" testid="tu-expiry" />
            </label>
            <label className="block">
              <span className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Issue date <span className="text-slate-600 normal-case">(optional)</span></span>
              <DatePickerField value={issued} onChange={setIssued} placeholder="—" className="mt-1 w-full bg-white rounded-lg px-3 py-2 text-sm" testid="tu-issued" />
            </label>
          </div>
          <label className="block">
            <span className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Note <span className="text-slate-600 normal-case">(optional)</span></span>
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. St. John Ambulance, Standard First Aid level C" className={`${inputCls} mt-1`} data-testid="tu-note" />
          </label>
        </div>
        <div className="flex items-center justify-end gap-2 p-5 border-t border-slate-200">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm font-semibold bg-white border border-slate-300 text-slate-800 hover:bg-slate-100" data-testid="tu-cancel">Cancel</button>
          <button onClick={submit} disabled={saving || reading || !file || !employeeId || !name.trim()} className="px-4 py-2 rounded-lg text-sm font-semibold bg-blue-600 hover:bg-blue-500 disabled:opacity-40 text-white flex items-center gap-1.5" data-testid="tu-submit">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} {saving ? 'Filing…' : renewing ? 'File renewal' : 'File ticket'}
          </button>
        </div>
      </div>
    </div>, document.body);
}
